import type {
  CompareAlignBy,
  CompareMode,
  DashboardDateRangeMeta,
  DashboardFilters,
  DateGranularity,
  DatePreset,
} from "./contracts";

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface PreparedDateFilters {
  filters: DashboardFilters;
  dateRanges: DashboardDateRangeMeta;
  errors: string[];
  swappedPrimary: boolean;
  swappedCompare: boolean;
}

const DAY_MS = 86_400_000;
export const DEFAULT_TIME_ZONE = "Atlantic/Canary";
export const MAX_RANGE_DAYS = 731;

export const DATE_PRESETS: Array<{ value: DatePreset; label: string }> = [
  { value: "today", label: "Hoy" },
  { value: "yesterday", label: "Ayer" },
  { value: "last_7_days", label: "Últimos 7 días" },
  { value: "last_28_days", label: "Últimos 28 días" },
  { value: "last_30_days", label: "Últimos 30 días" },
  { value: "last_90_days", label: "Últimos 90 días" },
  { value: "this_week", label: "Esta semana" },
  { value: "last_week", label: "Semana pasada" },
  { value: "this_month", label: "Este mes" },
  { value: "last_month", label: "Mes pasado" },
  { value: "quarter_to_date", label: "Trimestre hasta la fecha" },
  { value: "this_year", label: "Este año" },
  { value: "previous_year", label: "Año anterior" },
  { value: "custom", label: "Personalizado" },
];

export const COMPARE_MODE_LABELS: Record<CompareMode, string> = {
  previous_period: "Período anterior",
  same_period_last_year: "Mismo período del año anterior",
  custom: "Personalizado",
};

export function createInitialDateFilters(
  timeZone = getBrowserTimeZone(),
  now = new Date(),
): Pick<
  DashboardFilters,
  | "startDate"
  | "endDate"
  | "datePreset"
  | "compareEnabled"
  | "compareMode"
  | "compareStartDate"
  | "compareEndDate"
  | "compareAlignBy"
  | "granularity"
  | "timezone"
> {
  const timezone = normalizeTimeZone(timeZone);
  const primary = getPresetRange("last_28_days", timezone, now);
  const compare = getPreviousPeriod(primary);
  return {
    ...primary,
    datePreset: "last_28_days",
    compareEnabled: true,
    compareMode: "previous_period",
    compareStartDate: compare.startDate,
    compareEndDate: compare.endDate,
    compareAlignBy: "date_index",
    granularity: computeGranularity(primary.startDate, primary.endDate),
    timezone,
  };
}

export function getBrowserTimeZone() {
  if (typeof Intl === "undefined") return DEFAULT_TIME_ZONE;
  return normalizeTimeZone(
    Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIME_ZONE,
  );
}

export function normalizeTimeZone(value?: string | null) {
  const candidate = value?.trim() || DEFAULT_TIME_ZONE;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: candidate }).format(
      new Date(),
    );
    return candidate;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
}

export function getTodayInTimeZone(timeZone: string, now = new Date()) {
  const timezone = normalizeTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  return `${year}-${month}-${day}`;
}

export function getPresetRange(
  preset: DatePreset,
  timeZone: string,
  now = new Date(),
  currentRange?: DateRange,
): DateRange {
  const today = getTodayInTimeZone(timeZone, now);
  const yesterday = addDays(today, -1);
  const todayParts = parseDateParts(today)!;

  switch (preset) {
    case "today":
      return { startDate: today, endDate: today };
    case "yesterday":
      return { startDate: yesterday, endDate: yesterday };
    case "last_7_days":
      return { startDate: addDays(today, -6), endDate: today };
    case "last_28_days":
      return { startDate: addDays(today, -27), endDate: today };
    case "last_30_days":
      return { startDate: addDays(today, -29), endDate: today };
    case "last_90_days":
      return { startDate: addDays(today, -89), endDate: today };
    case "this_week": {
      return { startDate: startOfWeek(today), endDate: today };
    }
    case "last_week": {
      const currentWeekStart = startOfWeek(today);
      const endDate = addDays(currentWeekStart, -1);
      return { startDate: addDays(endDate, -6), endDate };
    }
    case "this_month":
      return {
        startDate: formatDateParts(todayParts.year, todayParts.month, 1),
        endDate: today,
      };
    case "last_month": {
      const monthStart = formatDateParts(todayParts.year, todayParts.month, 1);
      const endDate = addDays(monthStart, -1);
      const endParts = parseDateParts(endDate)!;
      return {
        startDate: formatDateParts(endParts.year, endParts.month, 1),
        endDate,
      };
    }
    case "quarter_to_date": {
      const quarterMonth =
        Math.floor((todayParts.month - 1) / 3) * 3 + 1;
      return {
        startDate: formatDateParts(todayParts.year, quarterMonth, 1),
        endDate: today,
      };
    }
    case "this_year":
      return {
        startDate: formatDateParts(todayParts.year, 1, 1),
        endDate: today,
      };
    case "previous_year":
      return {
        startDate: formatDateParts(todayParts.year - 1, 1, 1),
        endDate: formatDateParts(todayParts.year - 1, 12, 31),
      };
    case "custom":
      return currentRange || { startDate: addDays(today, -27), endDate: today };
    default:
      return { startDate: addDays(today, -27), endDate: today };
  }
}

export function getPreviousPeriod(range: DateRange): DateRange {
  const days = daysInclusive(range.startDate, range.endDate);
  const endDate = addDays(range.startDate, -1);
  return {
    startDate: addDays(endDate, -(days - 1)),
    endDate,
  };
}

export function getSamePeriodLastYear(range: DateRange): DateRange {
  return {
    startDate: addYears(range.startDate, -1),
    endDate: addYears(range.endDate, -1),
  };
}

export function getComparisonRange(
  primary: DateRange,
  mode: CompareMode,
  customRange?: Partial<DateRange>,
): DateRange | null {
  if (mode === "previous_period") return getPreviousPeriod(primary);
  if (mode === "same_period_last_year") {
    return getSamePeriodLastYear(primary);
  }
  if (!customRange?.startDate || !customRange.endDate) return null;
  return normalizeDateOrder(customRange.startDate, customRange.endDate).range;
}

export function getComparisonDateRange(filters: DashboardFilters) {
  if (
    filters.compareEnabled === false ||
    !filters.compareStartDate ||
    !filters.compareEndDate
  ) {
    return null;
  }
  return {
    startDate: filters.compareStartDate,
    endDate: filters.compareEndDate,
  };
}

export function prepareDashboardDateFilters(
  input: DashboardFilters,
  options: {
    now?: Date;
    maxRangeDays?: number;
  } = {},
): PreparedDateFilters {
  const errors: string[] = [];
  const timeZone = normalizeTimeZone(input.timezone);
  const maxRangeDays = options.maxRangeDays || MAX_RANGE_DAYS;
  const now = options.now || new Date();
  const today = getTodayInTimeZone(timeZone, now);
  let swappedPrimary = false;
  let swappedCompare = false;

  if (!isDateString(input.startDate) || !isDateString(input.endDate)) {
    errors.push("El rango principal debe tener fechas válidas.");
  }

  const primaryOrder = normalizeDateOrder(input.startDate, input.endDate);
  swappedPrimary = primaryOrder.swapped;
  const primary = primaryOrder.range;

  validateRange(primary, {
    label: "El rango principal",
    today,
    allowFutureDates: Boolean(input.allowFutureDates),
    maxRangeDays,
    errors,
  });

  const compareEnabled =
    input.compareEnabled ??
    Boolean(input.compareStartDate && input.compareEndDate);
  const compareMode = input.compareMode || "previous_period";
  const compareAlignBy = input.compareAlignBy || "date_index";
  let compare: DateRange | null = null;

  if (compareEnabled) {
    const computed = getComparisonRange(primary, compareMode, {
      startDate: input.compareStartDate,
      endDate: input.compareEndDate,
    });
    if (!computed) {
      errors.push("El rango de comparación personalizado está incompleto.");
    } else {
      const compareOrder = normalizeDateOrder(
        computed.startDate,
        computed.endDate,
      );
      swappedCompare = compareOrder.swapped;
      compare = compareOrder.range;
      validateRange(compare, {
        label: "El rango de comparación",
        today,
        allowFutureDates: Boolean(input.allowFutureDates),
        maxRangeDays,
        errors,
      });
    }
  }

  const granularity = computeGranularity(primary.startDate, primary.endDate);
  const filters: DashboardFilters = {
    ...input,
    startDate: primary.startDate,
    endDate: primary.endDate,
    datePreset: input.datePreset || "custom",
    compareEnabled,
    compareMode,
    compareAlignBy,
    granularity,
    timezone: timeZone,
  };

  if (compare) {
    filters.compareStartDate = compare.startDate;
    filters.compareEndDate = compare.endDate;
  } else {
    delete filters.compareStartDate;
    delete filters.compareEndDate;
  }

  return {
    filters,
    dateRanges: {
      primary: {
        ...primary,
        ...getUtcBoundaries(primary, timeZone),
        granularity,
        timezone: timeZone,
      },
      compare: compare
        ? {
            ...compare,
            ...getUtcBoundaries(compare, timeZone),
            mode: compareMode,
            alignBy: compareAlignBy,
            granularity,
            timezone: timeZone,
          }
        : null,
    },
    errors,
    swappedPrimary,
    swappedCompare,
  };
}

export function computeGranularity(
  startDate: string,
  endDate: string,
): DateGranularity {
  const days = daysInclusive(startDate, endDate);
  if (days <= 90) return "day";
  if (days <= 365) return "week";
  return "month";
}

export function bucketDate(date: string, granularity: DateGranularity) {
  if (granularity === "day") return date;
  if (granularity === "week") return startOfWeek(date);
  const parts = parseDateParts(date);
  return parts ? formatDateParts(parts.year, parts.month, 1) : date;
}

export function daysInclusive(startDate: string, endDate: string) {
  const start = dateToUtcMs(startDate);
  const end = dateToUtcMs(endDate);
  if (start === null || end === null) return 0;
  return Math.max(1, Math.round((end - start) / DAY_MS) + 1);
}

export function normalizeDateOrder(startDate: string, endDate: string) {
  const swapped = isDateString(startDate) && isDateString(endDate)
    ? startDate > endDate
    : false;
  return {
    swapped,
    range: swapped
      ? { startDate: endDate, endDate: startDate }
      : { startDate, endDate },
  };
}

export function isDateString(value: string | undefined): value is string {
  return Boolean(parseDateParts(value));
}

export function addDays(date: string, amount: number) {
  const parts = parseDateParts(date);
  if (!parts) return date;
  const next = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  next.setUTCDate(next.getUTCDate() + amount);
  return formatDateFromUtc(next);
}

export function addYears(date: string, amount: number) {
  const parts = parseDateParts(date);
  if (!parts) return date;
  const targetYear = parts.year + amount;
  const day = Math.min(parts.day, daysInMonth(targetYear, parts.month));
  return formatDateParts(targetYear, parts.month, day);
}

export function startOfWeek(date: string) {
  const parts = parseDateParts(date);
  if (!parts) return date;
  const utc = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  const day = utc.getUTCDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  utc.setUTCDate(utc.getUTCDate() + mondayOffset);
  return formatDateFromUtc(utc);
}

export function getMonthOffset(date: string, amount: number) {
  const parts = parseDateParts(date);
  if (!parts) return date;
  const next = new Date(Date.UTC(parts.year, parts.month - 1 + amount, 1));
  return formatDateParts(next.getUTCFullYear(), next.getUTCMonth() + 1, 1);
}

export function getDaysInMonth(date: string) {
  const parts = parseDateParts(date);
  if (!parts) return 31;
  return daysInMonth(parts.year, parts.month);
}

export function getUtcBoundaries(range: DateRange, timeZone: string) {
  return {
    startUtc: zonedDateTimeToUtc(range.startDate, "00:00:00.000", timeZone)
      .toISOString(),
    endUtc: zonedDateTimeToUtc(range.endDate, "23:59:59.999", timeZone)
      .toISOString(),
  };
}

function validateRange(
  range: DateRange,
  options: {
    label: string;
    today: string;
    allowFutureDates: boolean;
    maxRangeDays: number;
    errors: string[];
  },
) {
  if (!isDateString(range.startDate) || !isDateString(range.endDate)) return;
  const days = daysInclusive(range.startDate, range.endDate);
  if (days > options.maxRangeDays) {
    options.errors.push(
      `${options.label} excede el límite de ${options.maxRangeDays} días.`,
    );
  }
  if (!options.allowFutureDates && range.endDate > options.today) {
    options.errors.push(`${options.label} no puede incluir fechas futuras.`);
  }
}

function zonedDateTimeToUtc(
  date: string,
  time: string,
  timeZone: string,
) {
  const parts = parseDateParts(date);
  if (!parts) return new Date(Number.NaN);
  const [hour = 0, minute = 0, second = 0] = time
    .split(":")
    .map((value) => Number(value.replace(/\D.*$/, "")));
  const millisecond = Number(time.split(".")[1] || 0);
  const guess = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    hour,
    minute,
    second,
    millisecond,
  );
  const offset = getTimeZoneOffsetMs(new Date(guess), timeZone);
  let utc = guess - offset;
  const refinedOffset = getTimeZoneOffsetMs(new Date(utc), timeZone);
  if (refinedOffset !== offset) {
    utc = guess - refinedOffset;
  }
  return new Date(utc);
}

function getTimeZoneOffsetMs(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: normalizeTimeZone(timeZone),
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  const localAsUtc = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second),
  );
  return localAsUtc - date.getTime();
}

function parseDateParts(value: string | undefined) {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const utc = new Date(Date.UTC(year, month - 1, day));
  if (
    utc.getUTCFullYear() !== year ||
    utc.getUTCMonth() + 1 !== month ||
    utc.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
}

function dateToUtcMs(date: string) {
  const parts = parseDateParts(date);
  return parts ? Date.UTC(parts.year, parts.month - 1, parts.day) : null;
}

function daysInMonth(year: number, month: number) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function formatDateFromUtc(date: Date) {
  return formatDateParts(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
  );
}

function formatDateParts(year: number, month: number, day: number) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
